/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { LineAreaChartProps, LineAreaItem } from './lineAreaChart.types';
import { findNearestDataPoint } from '../utils/PRIVATE_chartUtils/layoutUtils';
import { AreaSeries } from './AreaSeries';
import { getCumulativeSeries, getMarkers, getSeriesData } from '../utils/PRIVATE_chartUtils/utils';
import { Markers } from '../PRIVATE_Chart/Markers';
import { getLineAreaChartNavUtil } from '../utils/PRIVATE_chartUtils/navUtils';
import { Chart } from '../PRIVATE_Chart';
import { Scale, TimeAxisProps } from '../utils/UNSAFE_visTypes/chart';
import { useUser } from '../hooks/UNSAFE_useUser';
import { getColorRamp } from '../utils/UNSAFE_visUtils';
import { LineSeries } from './LineSeries';
import { Point } from '../utils/PRIVATE_visSVGUtils';
import { getLineAreaSegments } from '../utils/PRIVATE_chartUtils/utils';
import { colorSchemeVars } from '../Common/themes/themeContract.css';
import { getItemColor, getMarkerType } from '../utils/PRIVATE_chartUtils/dataStyleUtils';

const AXIS_OFFSET = 0.2;

/**
 * The LineAreaChart displays information graphically using lines and filled areas, making relationships among the data easier to understand.
 */
export function LineAreaChart<K extends string | number, D extends LineAreaItem<K>>({
  width,
  height,
  selectedIds = [],
  hiddenIds = [],
  highlightedIds,
  hideAndShowBehavior = 'none',
  orientation = 'vertical',
  yAxis,
  series,
  groups,
  isStacked = false,
  getDataItem,
  selectionMode = 'none',
  drilling = 'off',
  type = 'line',
  ...props
}: LineAreaChartProps<K, D>) {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const isLog = yAxis?.scale === 'log';
  const timeAxisType = (props.xAxis as TimeAxisProps)?.timeAxisType;
  const hiddenSet = new Set(hiddenIds);
  const selectedSet = new Set(selectedIds);
  const highlightedSet = new Set(highlightedIds);
  const isHoriz = orientation === 'horizontal';
  const colors = getColorRamp();
  const renderedSeries = isStacked
    ? getCumulativeSeries(series.length, groups.length, getDataItem, hiddenSet, isLog)
    : getSeriesData<K, D>(series?.length, groups?.length, getDataItem, hiddenSet, isLog);

  const renderedGetDataItem = (seriesIndex: number, groupIndex: number): D | undefined => {
    if (!isStacked) return getDataItem(seriesIndex, groupIndex);
    return {
      ...getDataItem(seriesIndex, groupIndex),
      value: renderedSeries[seriesIndex][groupIndex]
    } as D;
  };

  const getGapRatio = () => {
    return 1;
  };

  const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
    return getLineAreaChartNavUtil<K, D>(
      getDataItem,
      series.length,
      startIndex,
      endIndex,
      isStacked,
      isRtl,
      isHoriz
    );
  };

  const getDataItemPos = (xScale: Scale, yScale: Scale) => {
    return (seriesIndex: number, groupIndex: number) => {
      const value = renderedSeries[seriesIndex][groupIndex];
      let xVal = groupIndex;
      if (timeAxisType) {
        const val =
          timeAxisType === 'mixedFrequency'
            ? getDataItem(seriesIndex, groupIndex)?.x
            : groups[groupIndex].id;
        xVal = new Date(val!).getTime();
      }
      const x = isHoriz ? yScale.transform(value) : xScale.transform(xVal);
      const y = isHoriz ? xScale.transform(xVal) : yScale.transform(value);
      return { x, y, height: 0, width: 0 };
    };
  };

  const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
    return {
      color: getItemColor<K, D>(seriesIndex, groupIndex, series, getDataItem),
      type: getMarkerType<K>(seriesIndex, groupIndex, 'lineWithArea', getDataItem)
    };
  };

  const findNearest = (xScale: Scale, yScale: Scale) => {
    const getSeriesDataInfo = () => {
      return {
        offset: 0,
        dataWidth: 0
      };
    };

    const getYCoord = (value: number, yScale: Scale) => {
      return yScale.transform(value);
    };

    return findNearestDataPoint<K, D>(
      series,
      groups,
      xScale,
      yScale,
      renderedGetDataItem,
      isStacked,
      getSeriesDataInfo,
      getYCoord,
      isLog,
      0,
      hiddenSet,
      timeAxisType
    );
  };
  const markers = getMarkers();
  const areaOpacity = type === 'lineWithArea' ? 0.5 : 1;
  const isAreaRendered = type === 'area' || type === 'lineWithArea';
  const isContrastLineNeeded = type === 'area';
  const allLineSegmentCoords: Point[][] = [];
  return (
    <Chart
      width={width}
      height={height}
      selectedIds={selectedSet}
      hiddenIds={hiddenIds}
      hideAndShowBehavior={hideAndShowBehavior}
      orientation={orientation}
      yAxis={yAxis}
      series={series}
      groups={groups}
      selectionMode={selectionMode}
      drilling={drilling}
      isStacked={isStacked}
      getDataItem={getDataItem}
      findNearest={findNearest}
      getDataItemPos={getDataItemPos}
      getMarkerInfo={getMarkerColorAndType}
      renderGridLinesInFront={isAreaRendered}
      getNavUtil={getNextChartItemFunc}
      getGapRatio={getGapRatio}
      offset={AXIS_OFFSET}
      isRtl={isRtl}
      {...props}>
      {({
        xStartIndex,
        xEndIndex,
        xScale,
        yScale,
        activeId,
        focusedItemInfo,
        hoveredItemInfo,
        isPointInsideMarquee
      }) => {
        return (
          <>
            {renderedSeries.map((lineAreaSeries, index) => {
              if (lineAreaSeries.length === 0) {
                /* TODO: need a better way to handle when a series is hidden. */
                return;
              }
              const seriesItem = series[index];
              const lineSegmentCoords: Point[][] = getLineAreaSegments<K, D>(
                yScale,
                xScale,
                lineAreaSeries,
                xStartIndex,
                xEndIndex,
                isHoriz,
                isLog,
                groups,
                index,
                renderedGetDataItem,
                timeAxisType
              );
              allLineSegmentCoords[index] = lineSegmentCoords[0];
              const isBottomSegmentCurved =
                seriesItem.lineType === 'curved' &&
                isStacked &&
                renderedSeries.length > 1 &&
                index > 0;

              const isHighlighted =
                highlightedIds === undefined || highlightedSet.has(seriesItem.id);
              return (
                <>
                  {isAreaRendered && (
                    <AreaSeries
                      yScale={yScale}
                      lineType={seriesItem.lineType}
                      color={seriesItem.areaColor || colors[index % colors.length]}
                      isHorizontal={isHoriz}
                      areaColorOpacity={areaOpacity}
                      isBottomSegmentCurved={isBottomSegmentCurved}
                      bottomCoords={
                        isStacked && index != 0 ? allLineSegmentCoords[index - 1] : undefined
                      }
                      lineSegmentCoords={lineSegmentCoords}
                      isLog={isLog}
                      isHighlighted={isHighlighted}
                      seriesIndex={index}
                    />
                  )}
                  <LineSeries
                    lineType={seriesItem.lineType}
                    /* TODO: confirm with UX if this is contrastLine (changes depending on dark/light mode) or always white. 
                        It's always white in legacy.*/
                    lineColor={
                      isContrastLineNeeded ? colorSchemeVars.dvt.contrastLine : seriesItem.lineColor
                    }
                    /* The area generated in legacy are bit apart and not touching when stacked. Preact areas are a bit closer than legacy when curved. 
                      Increased the contrast linewidth (to 2.5 from legacy 1.25) to give legacy visual treatment.
                      TODO: investivage why legacy areas are not touching. maybe we want to do the same in preact svg utils that
                      generates this curve.
                     */
                    lineWidth={
                      isContrastLineNeeded ? (isStacked ? 2.5 : 1.25) : seriesItem.lineWidth
                    }
                    lineStyle={seriesItem.lineStyle}
                    color={seriesItem.lineColor || colors[index % colors.length]}
                    lineSegmentCoords={lineSegmentCoords}
                    isHorizontal={isHoriz}
                    isHighlighted={isHighlighted}
                    seriesIndex={index}
                  />
                  {
                    /*for non stacked charts, markers should render in the order of series.
                     some markers might get hidden behind area but this preserves the legacy behavior. */
                    !isStacked && (
                      <Markers
                        yScale={yScale}
                        xScale={xScale}
                        groups={groups}
                        series={seriesItem}
                        timeAxisType={timeAxisType}
                        data={lineAreaSeries}
                        color={
                          seriesItem.lineColor ||
                          seriesItem.areaColor ||
                          colors[
                            index % colors.length
                          ] /* TODO : make this use getItemColor function from dataStyleUtils. */
                        }
                        seriesIndex={index}
                        startIndex={xStartIndex}
                        endIndex={xEndIndex}
                        markerType={markers[index % markers.length]}
                        orientation={orientation}
                        isLog={isLog}
                        activeId={activeId}
                        getDataItem={getDataItem}
                        isPointInsideMarquee={isPointInsideMarquee}
                        selectedIds={selectedSet}
                        focusedItemIndex={
                          focusedItemInfo.seriesIndex === index &&
                          focusedItemInfo.isFocusVisible &&
                          !(props.isDataCursorEnabled && props.dataCursorStyle?.isMarkerDisplayed)
                            ? focusedItemInfo.groupIndex
                            : undefined
                        }
                        hoveredItemIndex={
                          hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                            ? hoveredItemInfo.groupIndex
                            : undefined
                        }
                        isSelectionEnabled={
                          selectionMode === 'single' || selectionMode === 'multiple'
                        }
                        isDrillEnabled={drilling === 'on'}
                      />
                    )
                  }
                </>
              );
            })}
            {isStacked &&
              renderedSeries.map((lineAreaSeries, index) => {
                if (lineAreaSeries.length === 0) {
                  /* TODO: need a better way to handle when a series is hidden.*/
                  return;
                }
                const seriesItem = series[index];
                return (
                  <Markers
                    yScale={yScale}
                    series={series[index]}
                    xScale={xScale}
                    groups={groups}
                    timeAxisType={timeAxisType}
                    data={lineAreaSeries}
                    color={
                      seriesItem.lineColor || seriesItem.areaColor || colors[index % colors.length]
                    }
                    seriesIndex={index}
                    isPointInsideMarquee={isPointInsideMarquee}
                    startIndex={xStartIndex}
                    endIndex={xEndIndex}
                    markerType={markers[index % markers.length]}
                    orientation={orientation}
                    isLog={isLog}
                    activeId={activeId}
                    getDataItem={getDataItem}
                    selectedIds={selectedSet}
                    focusedItemIndex={
                      focusedItemInfo.seriesIndex === index && focusedItemInfo.isFocusVisible
                        ? focusedItemInfo.groupIndex
                        : undefined
                    }
                    hoveredItemIndex={
                      hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                        ? hoveredItemInfo.groupIndex
                        : undefined
                    }
                    isSelectionEnabled={selectionMode === 'single' || selectionMode === 'multiple'}
                    isDrillEnabled={drilling === 'on'}
                  />
                );
              })}
          </>
        );
      }}
    </Chart>
  );
}
